import type { useQuery } from "@apollo/client/react";
import {
  CheckedStateRecord,
  FilterItemProps,
} from "@jana-technology/platform-ui";

import type {
  AssetSelectionFilterQuery,
  AssetSelectionQuery,
  AssetSelectionQueryVariables,
} from "$/__generated__/graphql";

import type { FilteredWellNamesByHierarchy } from "./types";

export const makeFilterItemProps = (
  items: NonNullable<
    NonNullable<
      useQuery.Result<AssetSelectionQuery, AssetSelectionQueryVariables>["data"]
    >["hierarchies"]
  >,
): Array<FilterItemProps> =>
  items.map((hierarchy, ind) => {
    const label = hierarchy?.name ?? `Unnamed Hierarchy ${ind + 1}`;

    const value =
      typeof hierarchy?.hierarchyId === "number"
        ? String(hierarchy.hierarchyId)
        : label;

    if (hierarchy?.items) {
      return {
        label,
        value,
        children: makeFilterItemProps(hierarchy.items),
      };
    }

    return {
      label,
      value,
    };
  });

export const flattenCheckboxValues = (
  items: Array<FilterItemProps>,
): Array<string> => {
  const result: Array<string> = [];
  const stack: Array<FilterItemProps> = [...items].reverse();

  while (stack.length) {
    const current = stack.pop()!;
    result.push(current.value);

    if (current.children?.length) {
      for (let i = current.children.length - 1; i >= 0; i -= 1) {
        stack.push(current.children[i]);
      }
    }
  }

  return result;
};

export const initHierarchyIdsCheckedState = (
  values: Array<string>,
): CheckedStateRecord => {
  return values.reduce<CheckedStateRecord>((acc, val) => {
    acc[val] = true;

    return acc;
  }, {});
};

export const radioTreeToBreadcrumbs = (
  tree: Array<FilterItemProps>,
  target: string,
): Array<FilterItemProps> => {
  const radioBreadcrumbs: Array<FilterItemProps> = [];

  const findByDepth = (nodes: Array<FilterItemProps>): boolean => {
    for (const node of nodes) {
      radioBreadcrumbs.push(node);

      // because well names are numeric, they collide with hierarchy ids
      // ! handle the selected condition with children until radio checkbox tree is fixed
      if (node.value === target || !node.children) {
        return true;
      }

      if (node.children && findByDepth(node.children)) {
        return true;
      }

      radioBreadcrumbs.pop();
    }

    return false;
  };

  findByDepth(tree);

  return radioBreadcrumbs;
};

export const generateRadioState = (
  radioTreeBreadcrumbs: Array<FilterItemProps>,
): Array<FilterItemProps> => {
  const reduced = radioTreeBreadcrumbs.reduceRight<FilterItemProps | null>(
    (acc, node) => {
      if (acc) {
        return { ...node, children: [acc] };
      }

      // leaf node
      return { value: node.value, label: node.label };
    },
    null,
  );

  return reduced ? [reduced] : [];
};

export const flattenRadioState = (
  radioState: Array<FilterItemProps>,
): Array<FilterItemProps> => {
  const flattened: Array<FilterItemProps> = [];
  const stack: Array<FilterItemProps> = [...radioState].reverse();

  while (stack.length) {
    const node = stack.pop()!;
    flattened.push(node);

    if (node.children?.length) {
      for (let i = node.children.length - 1; i >= 0; i -= 1) {
        stack.push(node.children[i]);
      }
    }
  }

  return flattened;
};

export const deriveRadioTreeBreadcrumbs = (
  hierarchiesWithWellNamesRadios: Array<FilterItemProps>,
  hierarchyId: string,
  wellName?: string,
): Array<FilterItemProps> => {
  const radioTreeBreadcrumbs: Array<FilterItemProps> = [];
  const parents = new Map<FilterItemProps, FilterItemProps | null>();
  const queue: Array<FilterItemProps> = [];

  hierarchiesWithWellNamesRadios.forEach((node) => {
    parents.set(node, null);
    queue.push(node);
  });

  let queueIndex = 0;

  while (queueIndex < queue.length) {
    const current = queue[queueIndex];
    queueIndex += 1;

    if (current.value === hierarchyId) {
      const path: Array<FilterItemProps> = [];
      let cursor: FilterItemProps | null | undefined = current;

      while (cursor) {
        path.push(cursor);
        cursor = parents.get(cursor);
      }

      radioTreeBreadcrumbs.length = 0;
      radioTreeBreadcrumbs.push(...path.reverse());

      if (current.children?.length) {
        // wellName is mandatory, so default it to the first one on hierarchyNode click
        const normalizedWellName = wellName ?? current.children[0].value;

        const wellNode = current.children.find(
          (child) => child.value === normalizedWellName,
        );

        if (wellNode) {
          radioTreeBreadcrumbs.push(wellNode);
        }
      }

      return radioTreeBreadcrumbs;
    }

    if (current.children?.length) {
      current.children.forEach((child) => {
        if (!parents.has(child)) {
          parents.set(child, current);
          queue.push(child);
        }
      });
    }
  }

  return radioTreeBreadcrumbs;
};

export const removeNodes = (num: number) => !Number.isNaN(num);
export const keepStrings = (item: unknown) => typeof item === "string";
export const stripTypenameField = <T extends object>(
  item: T & { __typename?: string },
) => {
  const { __typename, ...rest } = item;

  return rest;
};

// ? confirm filtering out null values; components not set up to handle nulls
export const initSelectedRiskAssetFiltersState = (
  baseFilters: AssetSelectionFilterQuery["assetSelectionFilter"],
) => ({
  mostRecentCorrosionLogs:
    baseFilters.mostRecentCorrosionLogs?.filter(keepStrings) ?? [],
  wellAgeRange: baseFilters.wellAgeRange ?? [],
  wellConfigurations: baseFilters.wellConfigurations?.filter(keepStrings) ?? [],
  wellTypes: baseFilters.wellTypes?.filter(keepStrings) ?? [],
  riskQuartiles: baseFilters.riskQuartiles?.map(stripTypenameField) ?? [],
});

export const joinHierarchyCheckboxesWithWellIds = (
  hierarchyItems: Array<FilterItemProps>,
  filteredWellNames: FilteredWellNamesByHierarchy,
): Array<FilterItemProps> => {
  const recurseOverHierarchyItems = (
    hierarchyItems: Array<FilterItemProps>,
  ): Array<FilterItemProps> => {
    return hierarchyItems.map((hierarchyCheckbox) => {
      const numValue = Number(hierarchyCheckbox.value);

      // can't be a match since values are ids (numbers)
      if (Number.isNaN(numValue)) {
        if (hierarchyCheckbox.children) {
          return {
            ...hierarchyCheckbox,
            children: recurseOverHierarchyItems(hierarchyCheckbox.children),
          };
        }

        return hierarchyCheckbox;
      }

      const matchingWell = filteredWellNames.find(
        (hierarchy) => hierarchy.hierarchyId === numValue,
      );

      if (matchingWell) {
        return {
          ...hierarchyCheckbox,
          children: matchingWell.wellNames.map((wellName) => ({
            label: wellName,
            value: `${numValue}_${wellName}`,
          })),
        };
      }

      return hierarchyCheckbox;
    });
  };

  return recurseOverHierarchyItems(hierarchyItems);
};

export const extractWellNameFromRadioValue = (value: string): string => {
  const parts = value.split("_");
  return parts.length > 1 ? parts[1] : parts[0];
};

export const findHierarchyName = (
  hierarchyId: number | null,
  hierarchies: NonNullable<
    NonNullable<
      useQuery.Result<AssetSelectionQuery, AssetSelectionQueryVariables>["data"]
    >["hierarchies"]
  >,
): string => {
  for (const item of hierarchies) {
    if (item.hierarchyId === hierarchyId) {
      return item.name ?? "";
    }
    if (item.items) {
      const name = findHierarchyName(hierarchyId, item.items);
      if (name) {
        return name;
      }
    }
  }
  return "";
};
